import { Box, Divider, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { UrlConstants } from "../../utils/CommonUtils/constants";
import * as Sentry from "@sentry/browser";
import useTranslationSetup from "../../utils/useTranslationSetup";
import AssessmentHeader from "../../components/assessmentHeader/AssessmentHeader";
import useAudioRecorderV3 from "../../utils/useAudioRecorderV3";
import AssessmentFooter from "../../components/assessmentFooter/AssessmentFooter";
import InstructionBackdrop from "../../components/instructionBackdrop/InstructionBackdrop";
import RetakeAudioDialog from "../../components/retakeAudioDialog/RetakeAudioDialog";
import SaveAudioConfirmationDrawer from "../../components/saveAudioConfirmationDrawer/SaveAudioConfirmationDrawer";
import AudioSizeShortError from "../../components/AudioSizeShortError/AudioSizeShortError";
import AudioSaveSuccessDialog from "../../components/audioSaveSuccessDialog/AudioSaveSuccessDialog";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CustomErrorDialogBox from "../../components/CustomErrorDialogBox/CustomErrorDialogBox";
import getUploadPresignedUrl from "../../fetchApi/getUploadPresignedUrl";
import uploadAudioUsingPresignedUrl from "../../fetchApi/uploadAudioUsingPresignedUrl";
import processAssessment from "../../fetchApi/processAssessment";
import useParamValues from "../../utils/useParamValues";
import useWebViewBridge from "../../utils/useWebViewBridge";
import allAssessmentUploaded from "../../fetchApi/allAssessmentAudioUploaded";
import WordDisplayBox from "../../components/WordBoxDisplay/WordDisplayBox";
import "./styles.css";

const AssessmentScreen = ({ createAssessmentResponse }) => {
  const {
    isRecording,
    isStopped,
    isProcessing,
    audioBlob,
    setAudioBlob,
    startRecording,
    stopRecording,
  } = useAudioRecorderV3();
  const { t, activeLanguage } = useTranslationSetup();
  const { studentName, token } = useParamValues();
  const { sendMessageToReactNative } = useWebViewBridge();
  const [fontSize, setFontSize] = useState(7.5); // Initial font size of 7.5vw
  const [duration, setDuration] = useState(0);
  const [audioBlobRef, setAudioBlobRef] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showBackButton, setShowBackButton] = useState(true);
  const [showSlider, setShowSlider] = useState(true);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openRetakeAudioDialog, setOpenRetakeAudioDialog] = useState(false);
  const [openSaveAudioConfirmationDrawer, setOpenSaveAudioConfirmationDrawer] =
    useState(false);
  const [audioSizeShort, setAudioSizeShort] = useState(false);
  const [audioSaveSuccessDialog, setAudioSaveSuccessDialog] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const assessmentUrl = `${
    UrlConstants.BASE_URL + UrlConstants.BASE_PATH_URL + UrlConstants.ASSESSMENT
  }`;
  const firstName = studentName ? studentName.split(" ")[0] : "";
  const abortController = useRef(null);

  async function handleAudioUpload(blob) {
    try {
      const preSignedUrl = await getUploadPresignedUrl(
        `${assessmentUrl}/${createAssessmentResponse?.id}/upload-presigned-url`,
        abortController,
        token
      );
      await uploadAudioUsingPresignedUrl(preSignedUrl, blob, abortController);
      await processAssessment(
        `${assessmentUrl}/${
          createAssessmentResponse?.id
        }/process-assessment?audio_type=${blob.type}`,
        abortController,
        token
      );
      await allAssessmentUploaded(
        `${assessmentUrl}/${
          createAssessmentResponse?.primary_assessment_id ??
          createAssessmentResponse?.id
        }/all-uploaded`,
        abortController,
        token
      );
      setAudioUploading(false);
      setAudioSaveSuccessDialog(true);
    } catch (error) {
      Sentry.captureException(error);
      setAudioUploading(false);
      setErrorDialogOpen(true);
    }
  }

  const handleStartRecording = () => {
    setOpenBackdrop(true);
    setTimeout(() => {
      if (!openBackdrop) {
        setShowSlider(false);
        setShowBackButton(false);
        startRecording();
      }
    }, 5500);
  };
  const handleStopRecording = () => {
    if (duration === undefined || (duration !== null && duration <= 5)) {
      setAudioSizeShort(true);
    }
    setShowBackButton(true);
    stopRecording();
  };
  const onClickAudioRecorder = () => {
    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };
  const handleAudioSizeErrorRetake = () => {
    setAudioSizeShort(false);
    setShowSlider(true);
    setAudioBlob(null);
  };
  const handleRetakeAudioRetake = () => {
    setOpenRetakeAudioDialog(false);
    setShowSlider(true);
    setAudioBlob(null);
  };

  const handleAudioSaveConfirmationPrimary = () => {
    if (audioBlobRef !== null) {
      audioBlobRef.current?.pause();
    }
    setAudioUploading(true);
    handleAudioUpload(audioBlob);
  };
  const handleAudioSaveConfirmationSecondary = () => {
    setOpenSaveAudioConfirmationDrawer(false);
    setShowSlider(true);
    setAudioBlob(null);
  };

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
  };

  const onClickAudioSaveSuccess = () => {
    sendMessageToReactNative("status=UPLOADED");
  };

  useEffect(() => {
    if (audioBlob !== null && duration <= 5) {
      setAudioSizeShort(true);
    }
  }, [duration, audioBlob]);

  return (
    <>
      <Box>
        <AssessmentHeader
          heading={`${t("student_orf_result", {
            firstName: firstName,
          })}`}
          onClick={() => {}}
        />
        <Divider className="assessment-screen-divider" />

        <Box className="assessment-container">
          <Box className="assessment-paragraph-wrapper">
            {showSlider && (
              <Box className="assessment-screen-text-overlay">
                <Typography className="assessment-screen-text-overlay-item">
                  {t("assessment_screen_paragraph_text_overlay")}
                </Typography>
              </Box>
            )}
            <Typography
              fontSize={`${fontSize}vw`}
              className={`assessment-paragraph ${showSlider ? "blur" : ""}`}
              lang={activeLanguage}
            >
              {createAssessmentResponse?.content_type === "words" ? (
                <WordDisplayBox
                  text={createAssessmentResponse?.paragraph}
                  fontSize={fontSize}
                />
              ) : (
                createAssessmentResponse?.paragraph
              )}
            </Typography>
          </Box>

          <AssessmentFooter
            fontSize={fontSize}
            setFontSize={setFontSize}
            isRecording={isRecording}
            isStopped={isStopped}
            audioBlob={audioBlob}
            showSlider={showSlider}
            setDuration={setDuration}
            onClickRetake={() => setOpenRetakeAudioDialog(true)}
            onClickAudioRecorder={onClickAudioRecorder}
            onClickSave={() => setOpenSaveAudioConfirmationDrawer(true)}
            isProcessing={isProcessing}
          />
        </Box>
      </Box>

      <InstructionBackdrop
        openBackdrop={openBackdrop}
        setOpenBackdrop={setOpenBackdrop}
      />
      <RetakeAudioDialog
        isDialogOpen={openRetakeAudioDialog}
        setDialogOpen={setOpenRetakeAudioDialog}
        setShowSlider={setShowSlider}
        setAudioBlob={setAudioBlob}
        onClickCancel={() => setOpenRetakeAudioDialog(false)}
        onClickRetake={handleRetakeAudioRetake}
      />
      <SaveAudioConfirmationDrawer
        openDrawer={openSaveAudioConfirmationDrawer}
        audioBlob={audioBlob}
        audioBlobRef={audioBlobRef}
        setAudioBlobRef={setAudioBlobRef}
        onClickPrimaryButton={handleAudioSaveConfirmationPrimary}
        onClickSecondaryButton={handleAudioSaveConfirmationSecondary}
        uploadDataLoading={audioUploading}
      />
      <AudioSizeShortError
        isDialogOpen={audioSizeShort}
        warningHeading={t("audio_size_small_heading")}
        warningMessage={t("audio_size_small_text")}
        onClickRetakeButton={handleAudioSizeErrorRetake}
      />
      <AudioSaveSuccessDialog
        isDialogOpen={audioSaveSuccessDialog}
        header={t("recording_saved_successfully")}
        description={t("will_notify_for_results")}
        btnText={t("choose_next_student")}
        onClick={onClickAudioSaveSuccess}
      />
      <CustomErrorDialogBox
        header={t("something_went_wrong")}
        icon={
          <ErrorOutlineOutlinedIcon
            style={{
              color: "#D95959",
              borderRadius: "10px",
              marginTop: "16px",
              width: "80px",
              height: "80px",
            }}
          />
        }
        primaryButtonText={t("retake")}
        secondaryButtonText={t("new_student")}
        isDialogOpen={errorDialogOpen}
        showPrimaryButton
        showSecondaryButton
        onClickPrimaryButton={handleRetryClick}
        onClickSecondaryButton={() => sendMessageToReactNative("status=FAILED")}
      />
    </>
  );
};

export default AssessmentScreen;
